import axios from "axios";
import type {AssertNoSpecDrift, Schemas, SpecDrift} from "api/schemaAssert";

/**
 * The ad-conversion read (api kitecorp/transferify-api-service#1241).
 *
 * Authenticated, and account-scoped by the api rather than by anything sent from here: there is no
 * parameter, so this call cannot ask about anybody but the signed-in caller.
 */

/**
 * Short, because this read stands between a purchase and the moment it can still be reported truthfully.
 * A browser `Purchase` carries no timestamp of its own, so a stalled connection is not worth waiting out —
 * the api's own upload reports the same purchase either way, with its true time.
 */
const READ_TIMEOUT_MS = 8_000;

/**
 * `AdPurchaseConversion` — one recorded purchase and the id it was reported under.
 *
 * `amountMinor` is what the purchase is WORTH as a conversion, not a receipt: on the pricing-dialog exit
 * offer the api values the sale at a regular month even though 0.99 was charged (api#1057). It is the
 * right figure to report to an ad platform and the wrong one to show a buyer.
 */
export interface AdPurchaseConversion {
	conversionId: string;
	amountMinor: number;
	currency: string;
}

/** `AdPurchaseConversions` — the caller's recent purchases, newest first. */
export interface AdPurchaseConversions {
	purchases: AdPurchaseConversion[];
}

/*
 * Compile-time bindings to the generated spec (api/schemaAssert.ts). A backend rename, retype or
 * nullability change fails `tsc -b` naming the field, instead of surfacing as an undefined at runtime —
 * which on this contract would mean firing `undefined` as a conversion id.
 */
export type _AdPurchaseConversion = AssertNoSpecDrift<
	SpecDrift<AdPurchaseConversion, Schemas["AdPurchaseConversion"]>>;
export type _AdPurchaseConversions = AssertNoSpecDrift<
	SpecDrift<AdPurchaseConversions, Schemas["AdPurchaseConversions"]>>;

export class AdsApi {
	/**
	 * `GET /ads/purchase-conversions` — what this account just bought, and under which conversion id.
	 *
	 * An EMPTY list is the ordinary answer and not a failure: it means the payment webhook that records
	 * the conversion has not landed yet, or that nothing was bought recently. The caller must do the same
	 * thing in both cases — report nothing, and ask again.
	 */
	static async recentPurchaseConversions(): Promise<AdPurchaseConversion[]> {
		const {data} = await axios.get<AdPurchaseConversions>("/ads/purchase-conversions",
			{timeout: READ_TIMEOUT_MS});
		return data.purchases;
	}
}
